import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ArrowUpDown, Filter } from 'lucide-react'
import { formatSentMessageTime } from '@/lib/time-utils'
import { SkeletonChatList } from '@/components/ui/skeleton-chat-list'
import type { ChatWithUserInfo } from '@/hooks/useArtistBookingDashboard'
import type { ChatStatus } from '@/types/models'

export type SortOption = 'booking_priority' | 'recent'
export type StatusFilter = 'all' | 'artist_message_needed' | 'waiting_for_client' | 'booked'

interface BookingChatListProps {
  chats: ChatWithUserInfo[]
  statusFilter?: StatusFilter
  onStatusFilterChange?: (filter: StatusFilter) => void
}

export function BookingChatList({ chats, statusFilter: externalStatusFilter, onStatusFilterChange }: BookingChatListProps) {
  const navigate = useNavigate()
  const [sortBy, setSortBy] = useState<SortOption>('recent')
  const [internalStatusFilter, setInternalStatusFilter] = useState<StatusFilter>('all')
  const [statusFilterPopoverOpen, setStatusFilterPopoverOpen] = useState(false)
  const [sortByPopoverOpen, setSortByPopoverOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  
  // Use external filter if provided, otherwise use internal filter
  const statusFilter = externalStatusFilter !== undefined ? externalStatusFilter : internalStatusFilter
  
  const handleStatusFilterChange = (filter: StatusFilter) => {
    if (externalStatusFilter !== undefined) {
      // If external filter is provided, use the callback
      onStatusFilterChange?.(filter)
    } else {
      // Otherwise, use internal state
      setInternalStatusFilter(filter)
    }
  }

  const handleChatClick = (chat: ChatWithUserInfo) => {
    // Don't navigate if it's a demo chat
    if (chat.id.startsWith('demo_chat_')) {
      return
    }
    navigate(`/chats/${chat.id}`)
  }

  // Get user display name from userInfo
  const getUserDisplayName = (chat: ChatWithUserInfo): string => {
    if (!chat.userInfo) return 'Unknown User'
    
    if (chat.userInfo.instagram) {
      return chat.userInfo.instagram.replace(/^@/, '')
    }
    
    if (chat.userInfo.email) {
      return chat.userInfo.email
    }
    
    if (chat.userInfo.displayName) {
      return chat.userInfo.displayName
    }
    
    return 'Unknown User'
  }

  // Get last message sender and content
  const getLastMessageInfo = (chat: ChatWithUserInfo): { sender: string; content: string; isUnread: boolean } => {
    if (!chat.messages || chat.messages.length === 0) {
      return { sender: '', content: 'No messages', isUnread: false }
    }

    const lastMessage = chat.messages[chat.messages.length - 1]
    const isUnread = !lastMessage.isRead
    
    let sender: string
    if (lastMessage.senderType === 'artist') {
      sender = 'You'
    } else {
      sender = getUserDisplayName(chat)
    }
    
    const content = lastMessage.type === 'image' ? '📷 Image' : lastMessage.content

    return { sender, content, isUnread }
  }

  // Sort and filter chats
  const sortedAndFilteredChats = useMemo(() => {
    let filtered = chats

    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      filtered = filtered.filter(chat => {
        const userInfo = chat.userInfo
        if (!userInfo) return false
        return (
          userInfo.instagram?.toLowerCase().includes(searchLower) ||
          userInfo.displayName?.toLowerCase().includes(searchLower) ||
          userInfo.email?.toLowerCase().includes(searchLower)
        )
      })
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'booked') {
        // Filter for chats that have a booking
        filtered = filtered.filter(chat => chat.hasBooking)
      } else {
        // Filter by status
        filtered = filtered.filter(chat => chat.status === statusFilter)
      }
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'booking_priority') {
        // Booking Priority: sort by upcoming booking date first
        // Chats with bookings come first, sorted by booking date
        const aHasBooking = !!a.booking
        const bHasBooking = !!b.booking
        
        if (aHasBooking && bHasBooking) {
          // Both have bookings, sort by scheduled timestamp
          return a.booking!.scheduledTimestamp.getTime() - b.booking!.scheduledTimestamp.getTime()
        } else if (aHasBooking) {
          // Only a has booking, a comes first
          return -1
        } else if (bHasBooking) {
          // Only b has booking, b comes first
          return 1
        } else {
          // Neither has booking, sort by status priority then recency
          const priorityOrder: Record<ChatStatus, number> = {
            artist_message_needed: 0,
            waiting_for_client: 1,
            active: 2,
            booked: 3,
            archived: 4
          }
          
          const priorityDiff = priorityOrder[a.status] - priorityOrder[b.status]
          if (priorityDiff !== 0) return priorityDiff
          
          return b.lastMessageAt.getTime() - a.lastMessageAt.getTime()
        }
      } else {
        // Recent: sort by last message time
        return b.lastMessageAt.getTime() - a.lastMessageAt.getTime()
      }
    })

    return sorted
  }, [chats, sortBy, statusFilter, searchQuery])

  const getStatusColor = (status: ChatStatus) => {
    switch (status) {
      case 'waiting_for_client':
        return 'text-orange-500'
      case 'artist_message_needed':
        return 'text-red-500'
      default:
        return 'text-gray-500'
    }
  }

  const getStatusLabel = (status: ChatStatus) => {
    switch (status) {
      case 'waiting_for_client':
        return 'Waiting on client'
      case 'artist_message_needed':
        return 'Needs response'
      default:
        return status
    }
  }

  const formatTime = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    
    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else if (days === 1) {
      return 'Yesterday'
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' })
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    }
  }

  const headerAndFilters = (
  <div className="mx-auto w-full">
      {/* Search bar */}
      <div className="relative mb-4">
        <input
          type="text"
          placeholder="Search messages..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-3 bg-muted/50 rounded-lg border border-border/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0 transition-colors"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        {/* Sort dropdown pill */}
        <Popover open={sortByPopoverOpen} onOpenChange={setSortByPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              className={`px-4 py-2 rounded-full text-xs font-medium transition-colors flex items-center gap-2 flex-shrink-0 ${
                sortBy === 'booking_priority'
                  ? 'bg-primary text-primary-foreground'
                  : sortBy === 'recent'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <ArrowUpDown className="w-4 h-4" />
              {sortBy === 'booking_priority' ? 'Booking Priority' : 'Recent'}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-1" align="start">
            <button
              onClick={() => {
                setSortBy('booking_priority')
                setSortByPopoverOpen(false)
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent ${
                sortBy === 'booking_priority'
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-foreground'
              }`}
            >
              Booking Priority
            </button>
            <button
              onClick={() => {
                setSortBy('recent')
                setSortByPopoverOpen(false)
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent ${
                sortBy === 'recent'
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-foreground'
              }`}
            >
              Recent
            </button>
          </PopoverContent>
        </Popover>

        {/* Status filter dropdown pill */}
        <Popover open={statusFilterPopoverOpen} onOpenChange={setStatusFilterPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              className={`px-4 py-2 rounded-full text-xs font-medium transition-colors flex items-center gap-2 flex-shrink-0 ${
                statusFilter === 'all'
                  ? 'bg-muted text-muted-foreground hover:bg-muted/80'
                  : statusFilter === 'artist_message_needed'
                  ? 'bg-red-500 text-white'
                  : statusFilter === 'waiting_for_client'
                  ? 'bg-orange-500 text-white'
                  : statusFilter === 'booked'
                  ? 'bg-green-500 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Filter className="w-4 h-4" />
              {statusFilter === 'all' ? 'All' : 
               statusFilter === 'artist_message_needed' ? 'Needs Response' : 
               statusFilter === 'waiting_for_client' ? 'Waiting' :
               statusFilter === 'booked' ? 'Booked' : 'All'}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-1" align="start">
            <button
              onClick={() => {
                handleStatusFilterChange('all')
                setStatusFilterPopoverOpen(false)
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent ${
                statusFilter === 'all' ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
              }`}
            >
              All
            </button>
            <button
              onClick={() => {
                handleStatusFilterChange('artist_message_needed')
                setStatusFilterPopoverOpen(false)
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent ${
                statusFilter === 'artist_message_needed' ? 'bg-red-50 text-red-700 font-medium' : 'bg-red-50 text-red-700'
              }`}
            >
              Needs Response
            </button>
            <button
              onClick={() => {
                handleStatusFilterChange('waiting_for_client')
                setStatusFilterPopoverOpen(false)
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent ${
                statusFilter === 'waiting_for_client' ? 'bg-orange-50 text-orange-700 font-medium' : 'bg-orange-50 text-orange-700'
              }`}
            >
              Waiting
            </button>
            <button
              onClick={() => {
                handleStatusFilterChange('booked')
                setStatusFilterPopoverOpen(false)
              }}
              className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent ${
                statusFilter === 'booked' ? 'bg-green-50 text-green-700 font-medium' : 'bg-green-50 text-green-700'
              }`}
            >
              Booked
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )

  const chatListContent = (
    <div className="mx-auto w-full">
      {/* Chat list */}
      <div className="overflow-hidden w-full">
        {sortedAndFilteredChats.length === 0 && searchQuery ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-6">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              No results found
            </h3>
            <p className="text-muted-foreground max-w-sm">
              Try searching with a different term
            </p>
          </div>
        ) : sortedAndFilteredChats.length === 0 ? (
          <SkeletonChatList itemCount={5} />
        ) : (
          <div className="flex flex-col gap-1">
            {sortedAndFilteredChats.map((chat) => {
              const { sender, content, isUnread } = getLastMessageInfo(chat)
              const userName = getUserDisplayName(chat)
              
              return (
                <div
                  key={chat.id}
                  onClick={() => handleChatClick(chat)}
                  className="grid grid-cols-[auto_1fr] h-20 md:h-18 hover:bg-muted/50 cursor-pointer transition-colors rounded-lg bg-card border border-border overflow-hidden"
                >
                  {/* Avatar / Icon - square, responsive size */}
                  <div className="relative">
                    <div className="w-20 h-20 md:w-18 md:h-18 overflow-hidden bg-primary/10 flex items-center justify-center">
                      {chat.userInfo?.avatarUrl ? (
                        <img
                          src={chat.userInfo.avatarUrl}
                          alt={chat.userInfo.displayName || chat.userInfo.email || chat.userInfo.instagram || 'User'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-foreground text-lg font-semibold">
                          {((chat.userInfo?.instagram || chat.userInfo?.email || chat.userInfo?.displayName || '?').replace(/^@/, '')[0] || '?').toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Chat content */}
                  <div className="min-w-0 flex flex-col py-1.5 pl-2 pr-2">
                    {/* Desktop layout */}
                    <div className="hidden md:flex items-start justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="font-medium text-foreground text-sm truncate">
                          {userName}
                        </h3>
                        {chat.hasBooking && (
                          <span className="text-sm font-medium text-green-500 pointer-events-none flex-shrink-0">
                            Booked
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-xs text-muted-foreground">
                          {formatTime(chat.lastMessageAt)}
                        </span>
                        {isUnread && (
                          <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
                        )}
                      </div>
                    </div>
                    
                    <div className="hidden md:flex items-center justify-between gap-2">
                      <p className={`text-sm line-clamp-2 ${isUnread ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                        {sender === 'You' ? formatSentMessageTime(chat.messages[chat.messages.length - 1].timestamp) : content}
                      </p>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {chat.status === 'artist_message_needed' && (
                          <span className={`text-sm font-medium ${getStatusColor(chat.status)} pointer-events-none`}>
                            {getStatusLabel(chat.status)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Mobile layout */}
                    <div className="md:hidden flex flex-col justify-between h-full">
                      {/* Row 1: Name and Time */}
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-foreground text-sm truncate">
                          {userName}
                        </h3>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          <span className="text-xs text-muted-foreground">
                            {formatTime(chat.lastMessageAt)}
                          </span>
                          {isUnread && (
                            <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
                          )}
                        </div>
                      </div>

                      {/* Row 2: Message preview */}
                      <p className={`text-sm line-clamp-1 ${isUnread ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                        {sender === 'You' ? formatSentMessageTime(chat.messages[chat.messages.length - 1].timestamp) : content}
                      </p>

                      {/* Row 3: Badges - Booked on left, Response status on right */}
                      <div className="flex items-center justify-between">
                        <div>
                          {chat.hasBooking && (
                            <span className="text-sm font-medium text-green-500 pointer-events-none">
                              Booked
                            </span>
                          )}
                        </div>
                        
                        <div className="flex-shrink-0">
                          {chat.status === 'artist_message_needed' && (
                            <span className={`text-sm font-medium ${getStatusColor(chat.status)} pointer-events-none`}>
                              {getStatusLabel(chat.status)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="mx-auto w-full">
      <div className="mb-4">
        {headerAndFilters}
      </div>
      {chatListContent}
    </div>
  )
}

